import React from 'react';
import classNames from 'classnames';
import { Heading, PropertyGroup } from '../../components';
import { COLOUR_HEX } from '../../components/ColorSwatchFilter/colorMap';

import css from './ListingPage.module.css';

// Listing-field key for the universal colour filter. Hard-coded here rather
// than imported from configListing.js to avoid a heavy circular import chain
// for what is purely a presentation switch.
const COLOUR_FAMILY_KEY = 'colour_family';

/**
 * Renders the Colour multi-enum section with a coloured dot per option
 * instead of the generic checkmark icon — designers parse colour palettes
 * visually, the tick is redundant in this context.
 *
 * Note on the option shape: pickCustomFieldProps in src/util/fieldHelpers.js
 * (line 131-132) rewrites enumOptions from {option, label} to {key, label}
 * before they reach this component. So we filter + look up hex by `o.key`,
 * not `o.option`. PropertyGroup uses the same {key, label} contract.
 */
const ColourDotList = ({ options, selectedOptions, twoColumns }) => {
  const selected = options.filter(o => selectedOptions.includes(o.key));
  return (
    <ul
      className={classNames(css.colourDotList, { [css.colourDotListTwoColumns]: twoColumns })}
    >
      {selected.map(o => {
        const hex = COLOUR_HEX[o.key];
        return (
          <li key={o.key} className={css.colourDotItem}>
            <span
              className={css.colourDot}
              style={hex ? { backgroundColor: hex } : undefined}
              aria-hidden="true"
            />
            <span className={css.colourDotLabel}>{o.label}</span>
          </li>
        );
      })}
    </ul>
  );
};

const SectionMultiEnumMaybe = props => {
  const { heading, options, selectedOptions, fieldKey } = props;
  // Matterax: the listing page NEVER shows unselected enum options. The
  // upstream prop is intentionally ignored — both `pickCustomFieldProps`
  // (in `src/util/fieldHelpers.js`) and the Sharetribe Console hosted
  // listing-field config can flip `showConfig.unselectedOptions` to true,
  // which gives the boat-rental "show full amenity list, tick included"
  // UX. For a materials catalogue that reads as noise (e.g. a colour
  // section showing 24 colours with 2 ticked + 22 strikethrough). We
  // force-hide unselected here so neither config layer can re-enable it.
  // ProfilePage has its own SectionMultiEnumMaybe and is unaffected.
  const showUnselectedOptions = false;
  const hasContent = selectedOptions?.length > 0;
  if (!heading || !options || !hasContent) {
    return null;
  }

  const isColour = fieldKey === COLOUR_FAMILY_KEY;
  const twoColumns = selectedOptions.length > 5;

  return (
    <section className={css.sectionMultiEnum}>
      <Heading as="h2" rootClassName={css.sectionHeading}>
        {heading}
      </Heading>
      {isColour ? (
        <ColourDotList
          options={options}
          selectedOptions={selectedOptions}
          twoColumns={twoColumns}
        />
      ) : (
        <PropertyGroup
          id="ListingPage.amenities"
          options={options}
          selectedOptions={selectedOptions}
          twoColumns={twoColumns}
          showUnselectedOptions={showUnselectedOptions}
        />
      )}
    </section>
  );
};

export default SectionMultiEnumMaybe;
